// --------------------------------------------------------------
// Created On: 2025-04-24
// Author: Zachary Thomas
//
// Last Modified: 2025-05-30
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import { GRAPH_DEFAULT_SERIES_COLORS } from "../../constants/miscellaneous";

// Given a single series figure out which color it should use for the y-axis.
// A colored y-axis indicates a pairing with a specific series that is displayed on the graph with the same color.
// A y-axis that is black is used when a pairing does not need to be explicitly clarified ("#000000").
// The following are cases where we want a black axis:
// - There is only one series on the graph.
// - The series is one of multiple series that shares the same y-axis.
// - The series index is outside of the bounds of the possible colors (should not be possible in normal operation).
export default function getSeriesYAxisColor(
  seriesIndex: number,
  seriesUnitName: string,
  seriesUnitMap: SeriesUnitMap,
  axisMode: AxisMode,
  seriesLength: number
): string {
  const black = "#000000";
  const seriesColor = GRAPH_DEFAULT_SERIES_COLORS[seriesIndex];
  if (seriesLength === 1) {
    return black;
  }
  if (seriesIndex >= GRAPH_DEFAULT_SERIES_COLORS.length) {
    return black;
  }
  switch (axisMode) {
    case "Y_AXIS_ATTRIBUTE":
      return seriesColor;
    case "Y_AXIS_UOM": {
      if (seriesUnitName in seriesUnitMap) {
        if (Object.keys(seriesUnitMap[seriesUnitName]).length > 1) {
          return black;
        }
      }
      return seriesColor;
    }
    default:
      return seriesColor;
  }
}

interface SeriesUnitMap {
  [key: string]: {
    // The dynamic key is the unit name.
    [key: string]: {
      // The dynamic key is the series index number.
      thisSeriesDrawsAxis: boolean; // Whether this specific series is generating the y-axis.
    };
  };
}

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";
